import { useMemo, useState } from 'react';
import {
	Button,
	Field,
	Input,
	makeStyles,
	Popover,
	PopoverSurface,
	PopoverTrigger,
	Tab,
	TabList,
	TabValue,
	tokens,
	Select
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { formatDateToTimeString, TimePicker } from '@fluentui/react-timepicker-compat';
import { timeframeOptions, timeOptions } from '../conditions/field-meet-certain-criteria/use-field-meet-certain-criteria';


interface InputProps {
	type: 'datetime' | 'timeframe' | 'undefined';
	dateTime: {
		value: Date | null;
		onChange(value: Date): void;
	};
	timeframe: {
		value: {
			timeNumber: number;
			timeUnit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';
			timeOption: 'ago' | 'from-now';
		};
		onChange(value: InputProps['timeframe']['value']): void;
	}
}

type DateTimeFormInputProps = InputProps['dateTime'];

const DateTimeContent = (props: DateTimeFormInputProps) => {
	const classes = useDateTimeInputStyles();
	const timeValue = props.value ? formatDateToTimeString(props.value) : formatDateToTimeString(new Date());

	return (
		<div className={classes.contentTabRoot}>
			<Field>
				<DatePicker
					className={classes.input}
					placeholder="Select a date..."
					value={props.value}
					onSelectDate={(date) => {
						props.onChange(date!);
					}}
				/>
			</Field>
			<Field>
				<TimePicker
					className={classes.input}
					placeholder="Select a date..."
					value={timeValue}
					onTimeChange={(_, data) => {
						const newValue = new Date(props.value!);
						newValue.setHours(data.selectedTime!.getHours());
						newValue.setMinutes(data.selectedTime!.getMinutes());
						newValue.setSeconds(data.selectedTime!.getSeconds());

						props.onChange(newValue);
					}}
				/>
			</Field>
		</div>
	);
};

const TimeframeContent = (props: InputProps['timeframe']) => {
	const classes = useDateTimeInputStyles();

	return (
		<div className={classes.contentTabRoot}>
			<Field>
				<Input
					id='timeframe-time-number'
					type="number"
					placeholder="Set a number"
					className={classes.input}
					appearance='outline'
					value={props.value.timeNumber.toString()}
					onChange={(ev) => {
						const value = ev.target.value;
						const isNumber = !isNaN(parseInt(value));

						if (isNumber) {
							props.onChange({
								...props.value,
								timeNumber: parseInt(ev.target.value)
							});
						}
						
						if (!isNumber) {
							props.onChange({
								...props.value,
								timeNumber: 0
							});
						}
					}}
				/>
			</Field>
			<Field>
				<Select
					className={classes.select}
					// placeholder="Select a unit"
					appearance='outline'
					// positioning='below-start'
					inputMode='none'
					value={
						timeframeOptions.find(item => item.value === props.value.timeUnit)?.value}
					onChange={(_, data) => {
						props.onChange({
							...props.value,
							timeUnit: data.value as 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'
						});
					}}
				>
					{timeframeOptions.map(item => (
						<option key={item.value} value={item.value}>{item.label}</option>
					))}
				</Select>
			</Field>
			<Field>
				<Select
					className={classes.select}
					appearance='outline'
					inputMode='none'
					value={timeOptions.find(item => item.value === props.value.timeOption)?.value}
					onChange={(_, data) => {
						props.onChange({
							...props.value,
							timeOption: data.value as 'ago' | 'from-now'
						});
					}}
				>
					<div style={{maxHeight: 240}}>
						{timeOptions.map(item => (
							<option key={item.value} value={item.value}>{item.label}</option>
						))}
					</div>
				</Select>
			</Field>
		</div>
	);
};

const formatDateToString = (date: Date) => {
	return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function DateTimeFormInput(props: InputProps) {
	const classes = useDateTimeInputStyles();
	const [selectedTab, setSelectedTab] = useState<TabValue>(props.type === 'datetime' ? 'datetime' : 'timeframe');

	const placeholder = useMemo(() => {
		if (props.type === 'datetime') {
			return props.dateTime.value ? formatDateToString(props.dateTime.value) : 'Select a date...';
		}

		if (props.type === 'timeframe') {
			return props.timeframe.value.timeNumber + ' ' +
				(timeframeOptions.find(item => item.value === props.timeframe.value.timeUnit)?.label || props.timeframe.value.timeUnit).toLowerCase() + ' ' +
				(timeOptions.find(item => item.value === props.timeframe.value.timeOption)?.label || props.timeframe.value.timeOption).toLowerCase();
		}

		return 'Set a value...';
	}, [
		props.type,
		props.dateTime.value,
		props.timeframe.value
	]);

	return (
		<>
			<Popover trapFocus withArrow>
				<PopoverTrigger disableButtonEnhancement>
					<Button
						appearance='outline'
						className={classes.triggerButton}
					>
						{placeholder}
					</Button>
				</PopoverTrigger>
				<PopoverSurface>
					<TabList
						selectedValue={selectedTab}
						onTabSelect={(_, data) => setSelectedTab(data.value)}
					>
						<Tab value="datetime">Date/time</Tab>
						<Tab value="timeframe">Timeframe</Tab>
					</TabList>
					{selectedTab === 'datetime' && ( 
						<DateTimeContent
							onChange={props.dateTime.onChange}
							value={props.dateTime.value}
						/>
					)}
					{selectedTab === 'timeframe' && (
						<TimeframeContent
							onChange={props.timeframe.onChange}
							value={props.timeframe.value}
						/>
					)}
				</PopoverSurface>
			</Popover>
		</>
	);
}

const useDateTimeInputStyles = makeStyles({
	triggerButton: {
		width: '100%',
		fontWeight: tokens.fontWeightRegular,
		justifyContent: 'flex-start',
	},
	dateTimeInputRoot: {
		display: 'flex',
		width: '100%',
		gap: '16px'
	},
	input: {
		minWidth: '120px',
		width: '100%',
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		outline: 'none',
		'::after': {
			borderBottom: 'none',
			height: 0,
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':focus-within': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1}`,
			borderBottomColor: `${tokens.colorNeutralStroke1}!important`,
			borderTopColor: `${tokens.colorNeutralStroke1}!important`,
			borderRightColor: `${tokens.colorNeutralStroke1}!important`,
			borderLeftColor: `${tokens.colorNeutralStroke1}!important`,
		},
	},
	select: {
		minWidth: '120px',
		width: '100%',
		outline: 'none',
		'& select': {
			borderBottomColor: `${tokens.colorNeutralStroke1}!important`,
		},
		'::after': {
			height: 0,
		},
		':focus-within': {
			background: tokens.colorNeutralBackground4Hover,
			borderBottomColor: `${tokens.colorNeutralStroke1}!important`,
			borderTopColor: `${tokens.colorNeutralStroke1}!important`,
			borderRightColor: `${tokens.colorNeutralStroke1}!important`,
			borderLeftColor: `${tokens.colorNeutralStroke1}!important`,
		},
	},
	contentTab: {
		padding: '16px',
	},
	contentTabRoot: {
		display: 'flex',
		flexDirection: 'row',
		gap: '16px',
		padding: '16px 8px',
		width: '420px',
	}
});
